import React, { useCallback, useEffect, useState } from "react";
import { Tag, Select, Row, Col } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import _ from "lodash";

import useFormatting from "hooks/useFormatting";
import DatePicker from "components/UI/DatePicker";
import "./index.css";

const getTimestamp = (value) => {
  return value.startOf("day").valueOf();
};

const MultipleDatePicker = ({
  value: selectedDate = [],
  index = 0,
  max = 10, //initial limit multiple picker
  noLimit = false, //remove limit multiple picker
  onChange = () => {},
  selectProps = {},
  datePickerProps = {},
  disabled = false,
  isHalfDay = false,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { formatDate } = useFormatting();

  const onValueChange = useCallback(
    (date) => {
      if (selectedDate.length <= max || noLimit) {
        const t = getTimestamp(date);
        const index = selectedDate.map((e) => dayjs(e).valueOf()).indexOf(t);
        const clone = _.clone(selectedDate);
        if (index > -1) {
          clone.splice(index, 1);
        } else {
          if (selectedDate.length < max) {
            clone.push(t);
          }
        }
        onChange && onChange(clone);
      }
    },
    [max, noLimit, onChange, selectedDate]
  );

  const handleLimit = useCallback(() => {
    if (max >= selectedDate.length || noLimit) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [max, selectedDate, noLimit]);

  const dateRender = (currentDate) => {
    const formattedDate = (selectedDate || []).map((e) =>
      getTimestamp(dayjs(e))
    );
    const isSelected = formattedDate.indexOf(getTimestamp(currentDate)) > -1;
    return (
      <div
        className={"ant-picker-cell-inner"}
        style={
          isSelected
            ? {
                position: "relative",
                zIndex: 2,
                display: "inlineBlock",
                width: "24px",
                height: "22px",
                lineHeight: "22px",
                backgroundColor: "#1890ff",
                color: "#fff",
                margin: "auto",
                borderRadius: "2px",
                transition: "background 0.3s, border 0.3s",
              }
            : {}
        }
      >
        {currentDate.date()}
      </div>
    );
  };

  const renderTag = ({ value, onClose }) => {
    const handleClose = () => {
      onClose();
      onChange && onChange(selectedDate.filter((t) => t !== value));
    };
    return (
      <Tag
        key={value}
        className="bg-blue2 text-white px-3 py-1 rounded-full font-semibold"
        closeIcon={
          !disabled ? (
            <CloseOutlined className="text-white text-xs ml-1 hover:opacity-70 hover:cursor-pointer" />
          ) : null
        }
        onClose={!disabled ? handleClose : null}
        closable={!disabled}
      >
        {formatDate(dayjs(value))}
      </Tag>
    );
  };

  const handleClickOutside = useCallback(
    (event) => {
      const dropdown = document.getElementsByClassName(
        `multipleDropdownClassName-${index}`
      )[0];
      if (dropdown && !dropdown.contains(event.target)) {
        setOpen(false);
      }
    },
    [index]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <>
      <Row className="border-b border-gray-300">
        <Col span={22}>
          <Select
            {...selectProps}
            disabled={disabled}
            mode="multiple"
            value={selectedDate}
            onClear={() => onChange && onChange([])}
            tagRender={renderTag}
            variant="borderless"
            open={open}
            onFocus={handleLimit}
            popupMatchSelectWidth={false}
            suffixIcon={null}
            popupClassName={`multipleDropdownClassName-${index}`}
            dropdownStyle={{ height: "270px", width: "280px", minWidth: "0" }}
            dropdownRender={() => {
              return (
                <DatePicker
                  {...datePickerProps}
                  disabled={disabled}
                  format={(value) => ""}
                  onChange={onValueChange}
                  value={""}
                  showToday={false}
                  open
                  cellRender={dateRender}
                  style={{
                    ...datePickerProps.style,
                    visibility: "hidden",
                  }}
                  getPopupContainer={() =>
                    document.getElementsByClassName(
                      `multipleDropdownClassName-${index}`
                    )[0]
                  }
                />
              );
            }}
          />
        </Col>
        <Col span={2}>
          <div className="flex items-center justify-end h-full">
            <Tag className="bg-primary text-white px-3 py-1 rounded-full font-semibold">
              {`${isHalfDay ? selectedDate.length / 2 : selectedDate.length} ${
                selectedDate.length > 1 ? t("employee.day") : t("employee.days")
              }`}
            </Tag>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default MultipleDatePicker;
